import { create } from 'zustand'

declare const __DEPLOY__: string

export const isDebugEnvironment = import.meta.env.DEV || __DEPLOY__ === 'dev'

const STORAGE_KEY = 'debug-settings'

export const DEBUG_TOGGLES = {
  MOCK_SERVER: 'mockServer',
  SIMULATE_UNSUPPORTED_DEVICE: 'simulateUnsupportedDevice',
} as const

const DEFAULT_TOGGLES: Record<string, boolean> = {
  [DEBUG_TOGGLES.MOCK_SERVER]: false,
}

const DEFAULT_VALUES: Record<string, number | string> = {}

interface DebugState {
  isDebugMenuOpen: boolean
  toggles: Record<string, boolean>
  values: Record<string, number | string>
}

interface DebugActions {
  openDebugMenu: () => void
  closeDebugMenu: () => void
  setToggle: (key: string, value: boolean) => void
  setValue: (key: string, value: number | string) => void
  setMockServerEnabled: (enabled: boolean) => Promise<void>
  resetToDefaults: () => void
  getToggle: (key: string) => boolean
  getValue: (key: string) => number | string
  getStringValue: (key: string) => string
}

type DebugStore = DebugState & DebugActions

interface StoredData {
  toggles?: Record<string, boolean>
  values?: Record<string, number | string>
}

function loadFromStorage(): {
  toggles: Record<string, boolean>
  values: Record<string, number | string>
} {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed: StoredData = JSON.parse(saved)
      return {
        toggles: { ...DEFAULT_TOGGLES, ...parsed.toggles },
        values: { ...DEFAULT_VALUES, ...parsed.values },
      }
    }
  } catch {
    // Ignore parse errors
  }
  return { toggles: { ...DEFAULT_TOGGLES }, values: { ...DEFAULT_VALUES } }
}

function saveToStorage(
  toggles: Record<string, boolean>,
  values: Record<string, number | string>
) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ toggles, values }))
  } catch {
    // Ignore storage errors
  }
}

const initialData = loadFromStorage()

export const useDebugStore = create<DebugStore>((set, get) => ({
  isDebugMenuOpen: false,
  toggles: initialData.toggles,
  values: initialData.values,

  openDebugMenu: () => set({ isDebugMenuOpen: true }),
  closeDebugMenu: () => set({ isDebugMenuOpen: false }),

  setToggle: (key: string, value: boolean) => {
    const newToggles = { ...get().toggles, [key]: value }
    saveToStorage(newToggles, get().values)
    set({ toggles: newToggles })
  },

  setValue: (key: string, value: number | string) => {
    const newValues = { ...get().values, [key]: value }
    saveToStorage(get().toggles, newValues)
    set({ values: newValues })
  },

  setMockServerEnabled: async (enabled: boolean) => {
    const { setToggle } = get()
    setToggle(DEBUG_TOGGLES.MOCK_SERVER, enabled)

    if (enabled) {
      const { startMockServer } = await import('@/mocks/browser')
      await startMockServer()
    } else {
      const { stopMockServer } = await import('@/mocks/browser')
      stopMockServer()
    }
  },

  resetToDefaults: () => {
    saveToStorage(DEFAULT_TOGGLES, DEFAULT_VALUES)
    set({ toggles: { ...DEFAULT_TOGGLES }, values: { ...DEFAULT_VALUES } })
  },

  getToggle: (key: string) =>
    get().toggles[key] ?? DEFAULT_TOGGLES[key] ?? false,

  getValue: (key: string) => get().values[key] ?? DEFAULT_VALUES[key] ?? 0,

  getStringValue: (key: string) =>
    String(get().values[key] ?? DEFAULT_VALUES[key] ?? ''),
}))

// Initialize mock server from saved state on app load
export async function initializeDebugFromStorage() {
  if (!isDebugEnvironment) return

  const { toggles } = loadFromStorage()
  if (toggles[DEBUG_TOGGLES.MOCK_SERVER]) {
    try {
      const { startMockServer } = await import('@/mocks/browser')
      await startMockServer()
    } catch (error) {
      console.error('[Debug] Failed to start mock server:', error)
    }
  }
}
